import React, { useRef, useCallback, useLayoutEffect } from 'react';
import './HighlightTextarea.css';

// Matches the inline reference tokens the owner can drag in:
//   pictures      → "#picture1"
//   options/list  → "#options1", "#list2"
const TOKEN_RE = /#(?:(?:picture|options|list)\d+|bookingLink(?:\{[^}\n]+\})?)/gi;
const isDraggableToken = (t) => /^#(?:(?:picture|options|list)\d+|bookingLink(?:\{[^}\n]+\})?)$/i.test((t || '').trim());

const escapeHtml = (str) =>
  (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * A textarea that renders `#pictureN` tokens in a blue "link" style, and accepts
 * drag-and-drop of picture chips (which insert a token at the drop caret).
 *
 * It uses the classic mirror-div technique: a highlighted backdrop sits exactly
 * under a transparent textarea so the caret and text stay perfectly aligned.
 */
const HighlightTextarea = ({
  value = '',
  onChange,
  placeholder = '',
  rows = 4,
  className = '',
  id,
}) => {
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);

  const highlighted = escapeHtml(value).replace(
    TOKEN_RE,
    (m) => `<span class="ht-token">${m}</span>`
  );

  // Keep the backdrop scrolled in sync with the textarea.
  const syncScroll = useCallback(() => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    syncScroll();
  }, [value, syncScroll]);

  const insertAtCaret = useCallback(
    (token) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const needsSpaceBefore = start > 0 && !/\s/.test(value[start - 1]);
      const insert = (needsSpaceBefore ? ' ' : '') + token + ' ';
      const next = value.slice(0, start) + insert + value.slice(end);
      onChange?.(next);
      // Restore caret after React re-render.
      requestAnimationFrame(() => {
        const pos = start + insert.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    },
    [value, onChange]
  );

  const handleDrop = useCallback(
    (e) => {
      const token = e.dataTransfer.getData('text/plain');
      if (isDraggableToken(token)) {
        e.preventDefault();
        insertAtCaret(token.trim());
      }
    },
    [insertAtCaret]
  );

  const handleDragOver = (e) => {
    // Allow dropping picture tokens.
    if (e.dataTransfer.types?.includes('text/plain')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  return (
    <div className={`ht-wrap ${className}`}>
      <div className="ht-backdrop" ref={backdropRef} aria-hidden="true">
        <div
          className="ht-highlights"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: highlighted + '\n' }}
        />
      </div>
      <textarea
        id={id}
        ref={textareaRef}
        className="ht-input"
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onScroll={syncScroll}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        spellCheck={false}
      />
    </div>
  );
};

export default HighlightTextarea;
